import {ApolloError, gql} from '@apollo/client';
import {useEffect, useRef, useState} from 'react';

import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';

import type {
  AllConfigurableTaskDefinitionsPaginatedQuery,
  AllConfigurableTaskDefinitionsPaginatedQueryVariables,
} from './__generated/AllConfigurableTaskDefinitionsPaginatedQuery';

const PAGE_SIZE = 1000;

export const ALL_CONFIGURABLE_TASK_DEFINITIONS_PAGINATED_QUERY = gql`
  query AllConfigurableTaskDefinitionsPaginatedQuery($limitOffsetInput: LimitOffsetInput)
  @engComponent(name: WORKFLOWS)
  @skipAudit {
    processTaskDefinitionsSupportingPlatformFeatureListing(
      platformFeature: SCHEMA_CONFIG
      limitOffsetInput: $limitOffsetInput
    ) {
      totalCount
      processTaskDefinitions {
        ...ProcessTaskDefinitionGraphQL
      }
    }
  }
  ${getFragmentWrapper('ProcessTaskDefinition').fragment}
`;

export type PaginatedTaskDefinitionsResult = SkippableBenchlingQueryResult<
  AllConfigurableTaskDefinitionsPaginatedQuery,
  AllConfigurableTaskDefinitionsPaginatedQueryVariables
>;

export function useAllConfigurableTaskDefinitionsPaginated(options: {
  skip?: boolean;
}): PaginatedTaskDefinitionsResult {
  const {skip = false} = options;
  const fetchingRef = useRef(false);
  const [fetchError, setFetchError] = useState<ApolloError | null>(null);

  const result = useSkippableBenchlingQuery<
    AllConfigurableTaskDefinitionsPaginatedQuery,
    AllConfigurableTaskDefinitionsPaginatedQueryVariables
  >(ALL_CONFIGURABLE_TASK_DEFINITIONS_PAGINATED_QUERY, {
    variables: {limitOffsetInput: {limit: PAGE_SIZE, offset: 0}},
    skip,
    fetchPolicy: 'cache-and-network',
  });

  // Fetch remaining pages. The field policy merges each page into the cache.
  useEffect(() => {
    if (skip || result.type !== QueryResultType.SUCCESS || fetchingRef.current) {
      return;
    }

    const listing = result.data.processTaskDefinitionsSupportingPlatformFeatureListing;
    const loaded = listing.processTaskDefinitions.length;
    const total = listing.totalCount;

    if (loaded < total) {
      fetchingRef.current = true;
      result
        .fetchMore({variables: {limitOffsetInput: {limit: PAGE_SIZE, offset: loaded}}})
        .then(() => {
          fetchingRef.current = false;
        })
        .catch((e: ApolloError) => {
          fetchingRef.current = false;
          setFetchError(e);
        });
    }
  }, [skip, result]);

  if (fetchError) {
    // Need cast because typescript can't narrow, see below
    return {
      ...result,
      type: QueryResultType.ERROR,
      error: fetchError,
    } as PaginatedTaskDefinitionsResult;
  }

  // Only report SUCCESS once all pages are loaded.
  if (result.type === QueryResultType.SUCCESS) {
    const listing = result.data.processTaskDefinitionsSupportingPlatformFeatureListing;
    if (listing.processTaskDefinitions.length < listing.totalCount) {
      // `result` is a SuccessReturnType but we want a PendingReturnType so manually override the
      // fields to return pending info, and cast because typescript can't narrow to PendingReturnType
      // after the discriminant (`type`) has changed
      return {
        ...result,
        type: QueryResultType.PENDING,
        data: undefined,
        loading: true,
      } as PaginatedTaskDefinitionsResult;
    }
  }

  return result;
}
